<template>
  <div
    class="modal fade"
    id="pendingInvitationsModal"
    tabindex="-1"
    aria-labelledby="pendingInvitationsModalLabel"
    aria-hidden="true"
    data-bs-backdrop="static"
    data-bs-keyboard="false"
    ref="modalEl"
  >
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="pendingInvitationsModalLabel">
            <i class="bi bi-envelope-paper me-2"></i>
            Pending Invitations
          </h5>
        </div>

        <div class="modal-body">
          <p class="text-muted mb-3">
            You have {{ localInvitations.length }} pending invitation{{ localInvitations.length !== 1 ? 's' : '' }}.
          </p>

          <div v-if="error" class="alert alert-danger mb-3">
            {{ error }}
          </div>

          <div class="invitation-list">
            <div
              v-for="inv in localInvitations"
              :key="inv.id"
              class="invitation-card"
              :class="{ processing: processingId === inv.id }"
            >
              <div class="invitation-header">
                <strong>{{ inv.tenantName }}</strong>
                <span class="badge bg-secondary ms-2">{{ inv.roleName }}</span>
              </div>
              <div class="invitation-details">
                <small class="text-muted">
                  Invited by {{ inv.invitedByName || 'someone' }}
                  <span class="mx-1">&middot;</span>
                  Expires {{ formatDate(inv.expiresAt) }}
                </small>
              </div>
              <div class="invitation-actions mt-2">
                <button
                  class="btn btn-sm btn-primary me-2"
                  @click="accept(inv)"
                  :disabled="processingId !== null"
                >
                  <span v-if="processingId === inv.id && processingAction === 'accept'" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                  Accept
                </button>
                <button
                  class="btn btn-sm btn-outline-secondary"
                  @click="reject(inv)"
                  :disabled="processingId !== null"
                >
                  <span v-if="processingId === inv.id && processingAction === 'reject'" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                  Decline
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="skip" :disabled="processingId !== null">
            Skip for now
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
/* eslint-env vue/setup-compiler-macros */
import { ref, watch, onMounted, onBeforeUnmount } from 'vue'
import { Modal } from 'bootstrap'
import { UsersApi } from '@/api/users_api'

const props = defineProps({
  invitations: {
    type: Array,
    required: true
  }
})

const emit = defineEmits(['skipped', 'update:invitations'])

const modalEl = ref(null)
let bsModal = null

const localInvitations = ref([...props.invitations])
const processingId = ref(null)
const processingAction = ref(null)
const error = ref(null)

watch(() => props.invitations, (val) => {
  localInvitations.value = [...val]
  if (val.length > 0 && bsModal) {
    show()
  }
})

onMounted(() => {
  bsModal = new Modal(modalEl.value)
  if (localInvitations.value.length > 0) {
    show()
  }
})

onBeforeUnmount(() => {
  bsModal?.dispose()
})

function show() {
  error.value = null
  processingId.value = null
  processingAction.value = null
  bsModal?.show()
}

function hide() {
  bsModal?.hide()
}

async function accept(inv) {
  processingId.value = inv.id
  processingAction.value = 'accept'
  error.value = null

  try {
    await UsersApi.acceptInvitation(inv.id)
    const updated = localInvitations.value.filter(i => i.id !== inv.id)
    localInvitations.value = updated
    emit('update:invitations', updated)

    hide()
    window.location.reload()
  } catch (e) {
    console.error('Accept invitation error:', e)
    error.value = 'Failed to accept invitation. Please try again.'
  } finally {
    processingId.value = null
    processingAction.value = null
  }
}

async function reject(inv) {
  processingId.value = inv.id
  processingAction.value = 'reject'
  error.value = null

  try {
    await UsersApi.rejectInvitation(inv.id)
    const updated = localInvitations.value.filter(i => i.id !== inv.id)
    localInvitations.value = updated
    emit('update:invitations', updated)

    if (updated.length === 0) {
      hide()
      emit('skipped')
    }
  } catch (e) {
    console.error('Reject invitation error:', e)
    error.value = 'Failed to decline invitation. Please try again.'
  } finally {
    processingId.value = null
    processingAction.value = null
  }
}

function skip() {
  hide()
  emit('skipped')
}

function formatDate(dateString) {
  if (!dateString) return 'unknown'
  const date = new Date(dateString)
  const now = new Date()
  const diffDays = Math.ceil((date - now) / (1000 * 60 * 60 * 24))

  if (diffDays <= 0) return 'today'
  if (diffDays === 1) return 'tomorrow'
  if (diffDays <= 7) return `in ${diffDays} days`
  return date.toLocaleDateString()
}
</script>

<style scoped>
.invitation-list {
  max-height: 400px;
  overflow-y: auto;
}

.invitation-card {
  padding: 1rem;
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  margin-bottom: 0.75rem;
  transition: opacity 0.2s;
}

.invitation-card:last-child {
  margin-bottom: 0;
}

.invitation-card.processing {
  opacity: 0.7;
}

.invitation-header {
  display: flex;
  align-items: center;
  margin-bottom: 0.25rem;
}
</style>
